import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { isEmail, pluralize } from 'shared/lib'
import { type ITabItem, type IDropdownItem, toastStore } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import { RequestLoadData } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IResponseUserIntegration } from 'entities/Integrations/api/types'
import { IntegrationsApi } from 'entities/Integrations/api'
import { IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import { UsersApi } from 'entities/Users/api/users'
import type { IParamsCreateInviteItem, IResponseCreateInvitesError } from 'entities/Users/api/types'
import {
  RowInviteCandidate,
  EnumUserRoles,
  getAssignableRoleItems,
  getDefaultAssignableRole,
  canAssignRole,
  roleLabelsMap,
} from 'entities/Users'
import { getManageableInboxes } from 'entities/Inbox'
import { CombinedPriceInfoStore } from 'entities/Billing'
import { integrationSortOrder } from 'pages/settings/pages/integrations/lib/integrationSortOrder'
import { parseInvitesError } from 'pages/settings/pages/members/pages/addMembers/lib/parseInvitesError'
import { MembersPath } from 'pages/settings/pages/members/route/membersPath'

enum AddEmailResult {
  Success = 'success',
  Invalid = 'invalid',
  Duplicate = 'duplicate',
  Empty = 'empty',
}

export class AddMembersStore {
  private _inviteCandidates: RowInviteCandidate[] = []
  private _inputValue = ''
  private _isDropdownOpen = false
  private _userIntegrations: IResponseUserIntegration[] = []
  private _isLoadingIntegrations = false
  private _isSendingInvites = false
  private _activeTabKey = ''
  private _disposeTableTotalReaction: IReactionDisposer | null = null
  private _disposeSeatsReaction: IReactionDisposer | null = null
  private _disposeCloseDropdownReaction: IReactionDisposer | null = null
  private _focusTrigger = ''
  private _emailError: string | null = null
  private _pendingInviteEmails: Set<string> = new Set()
  readonly tableStore: TableStore<RowInviteCandidate>
  readonly combinedPriceInfoStore: CombinedPriceInfoStore

  constructor(private _entities: IEntitiesStore) {
    this.tableStore = new TableStore<RowInviteCandidate>({ key: 'InviteCandidates' })
    this.combinedPriceInfoStore = new CombinedPriceInfoStore({
      entities: this._entities,
    })
    makeAutoObservable(this)
  }

  init = async () => {
    this._reactionTableTotal()
    this._reactionSeats()
    this._reactionCloseDropdown()
    await Promise.all([
      this._loadIntegrations(),
      this._entities.inboxesStore.organizationInboxesInitPromise,
      this._loadPendingInviteEmails(),
      this._entities.usersStore.initMembersPromise,
    ])
  }

  private _reactionTableTotal = () => {
    this._disposeTableTotalReaction?.()
    this._disposeTableTotalReaction = reaction(
      () => this._inviteCandidates.length,
      (length) => {
        this.tableStore.setTotal(length)
      }
    )
  }

  private _reactionSeats = () => {
    this._disposeSeatsReaction?.()

    if (this.isTrial) return

    this._disposeSeatsReaction = reaction(
      () => ({
        paidSeatsCount: this.paidSeatsCount,
        seatsAvailable: this.combinedPriceInfoStore.seatsAvailable,
      }),
      ({ paidSeatsCount }) => {
        this._requestUpdatePriceData.debounceLoadData(paidSeatsCount)
      },
      {
        fireImmediately: true,
      }
    )
  }

  private _requestUpdatePriceData = new RequestLoadData<number>({
    requestCallback: async (config, paidSeatsCount) => {
      if (typeof paidSeatsCount !== 'number') return

      this.combinedPriceInfoStore.updatePriceData(0, paidSeatsCount)
    },
  })

  private _reactionCloseDropdown = () => {
    this._disposeCloseDropdownReaction?.()
    this._disposeCloseDropdownReaction = reaction(
      () => this.isEmailInputDisabled,
      (shouldDisable) => {
        if (shouldDisable && this._isDropdownOpen) {
          this.closeDropdown()
        }
      }
    )
  }

  get invitedCount() {
    return this._inviteCandidates.length
  }

  get paidSeatsCount() {
    return this._inviteCandidates.filter((candidate) => candidate.role !== EnumUserRoles.VIEW_ONLY)
      .length
  }

  get isLoadingPriceInfo() {
    return this.combinedPriceInfoStore.requestInfoStatus.status.isLoading
  }

  private _loadIntegrations = async () => {
    this._isLoadingIntegrations = true

    try {
      const {
        data: { userIntegrations = [] },
      } = await IntegrationsApi.getIntegrationsList()

      runInAction(() => {
        this._userIntegrations = userIntegrations

        if (this.integrationTabs.length > 0 && !this._activeTabKey) {
          this._activeTabKey = this.integrationTabs[0].key
        }
      })
    } catch (error) {
      console.error('Failed to load integrations:', error)
    } finally {
      runInAction(() => {
        this._isLoadingIntegrations = false
      })
    }
  }

  private _loadPendingInviteEmails = async () => {
    try {
      const { data } = await UsersApi.getOrganizationPendingInvitesAll()

      runInAction(() => {
        this._pendingInviteEmails = new Set(
          data.invitations.map((invite) => invite.email?.toLowerCase()).filter(Boolean)
        )
      })
    } catch (error) {
      console.error('Failed to load pending invitations:', error)
    }
  }

  private get _existingMemberEmails(): Set<string> {
    return new Set(
      this._entities.usersStore.membersList.map((user) => user.email?.toLowerCase()).filter(Boolean)
    )
  }

  private get _excludedEmails(): Set<string> {
    return new Set([...this._existingMemberEmails, ...this._pendingInviteEmails])
  }

  private _createAndAddCandidate = (email: string, crmUser?: IntegrationCrmUser) => {
    if (this.isEmailInputDisabled) return

    const candidate = new RowInviteCandidate({
      id: nanoid(),
      email,
      crmUser,
      role: getDefaultAssignableRole(this.inviterRole),
    })

    if ((this.isTrial || this.isTwilioNumberVendor) && this.ownerInbox) {
      candidate.toggleInbox(this.ownerInbox.id)
    } else if (!this.hideNewInbox) {
      candidate.setNewInbox()
    }

    this._inviteCandidates.push(candidate)
    this._inputValue = ''
    this._focusTrigger = nanoid()
  }

  private _addEmail = (email: string, setError = true): AddEmailResult => {
    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedEmail) return AddEmailResult.Empty

    if (!isEmail(trimmedEmail)) {
      if (setError) {
        this._emailError = 'Please enter a valid email address'
        toastStore.add({
          type: 'error',
          title: 'Email address is invalid',
          desc: 'Please enter a valid email and try again.',
        })
      }
      return AddEmailResult.Invalid
    }

    const isDuplicate = this._inviteCandidates.some((item) => item.email === trimmedEmail)
    if (isDuplicate) {
      if (setError) {
        this._emailError = 'This email has already been added'
        toastStore.add({
          type: 'error',
          title: 'User already exists',
          desc: 'This email is already invited.',
        })
      }
      return AddEmailResult.Duplicate
    }

    if (setError) {
      this._emailError = null
    }
    this._createAndAddCandidate(trimmedEmail)
    return AddEmailResult.Success
  }

  private _getFilteredCrmUsersForIntegration = (
    userIntegration: IResponseUserIntegration
  ): IntegrationCrmUser[] => {
    const searchLower = this._inputValue.trim().toLowerCase()
    const addedEmails = new Set(
      this._inviteCandidates.map((candidate) => candidate.email.toLowerCase())
    )

    return userIntegration.crm_users
      .filter((user) => {
        if (!user.email) return false

        const emailLower = user.email.toLowerCase()

        if (this._excludedEmails.has(emailLower)) return false
        if (addedEmails.has(emailLower)) return false
        if (user.user_id || user.mapped_user_id) return false

        if (!searchLower) return true

        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase()

        return fullName.includes(searchLower) || emailLower.includes(searchLower)
      })
      .map((user) => new IntegrationCrmUser(user))
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  private get _canManageOrganizationInboxes() {
    return this._entities.userPermissionsStore.getItem('can_manage_organization_inboxes')
  }

  get inboxes() {
    const manageableInboxes = getManageableInboxes({
      inboxes: this._entities.inboxesStore.organizationInboxes,
      userId: this._entities.usersStore.user?.id,
      canManageOrganizationInboxes: this._canManageOrganizationInboxes,
    })

    if (this.isTrial) {
      return manageableInboxes.filter((inbox) => !inbox.isTrialIdentifier)
    }

    return manageableInboxes
  }

  get ownerInbox() {
    const userId = this._entities.usersStore.user?.id
    if (!userId) return null

    return this.inboxes.find((inbox) => inbox.ownerId === userId) ?? this.inboxes[0] ?? null
  }

  get isLoadingInboxes() {
    return this._entities.inboxesStore.organizationInboxesLoading
  }

  get inviteCandidateRows() {
    return this._inviteCandidates
  }

  get inviterRole(): EnumUserRoles | undefined {
    return this._entities.usersStore.user?.role
  }

  get roleItems(): IDropdownItem[] {
    return getAssignableRoleItems(this.inviterRole)
  }

  get inputValue() {
    return this._inputValue
  }

  get isDropdownOpen() {
    return this._isDropdownOpen
  }

  get isPageLoading() {
    return this._isLoadingIntegrations || this.isLoadingInboxes
  }

  get isSendingInvites() {
    return this._isSendingInvites
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get focusTrigger() {
    return this._focusTrigger
  }

  get integrationTabs(): ITabItem<string>[] {
    return this._userIntegrations
      .filter((ui) => ui.is_active && ui.crm_users.length > 0)
      .sort(
        (a, b) =>
          (integrationSortOrder[a.integration.key] ?? Infinity) -
          (integrationSortOrder[b.integration.key] ?? Infinity)
      )
      .map((ui) => ({
        key: ui.integration.key,
        name: ui.integration.name,
        count: this._getFilteredCrmUsersForIntegration(ui).length,
      }))
  }

  get hasIntegrations() {
    return this.integrationTabs.length > 0
  }

  get filteredCrmUsers(): IntegrationCrmUser[] {
    const activeIntegration = this._userIntegrations.find(
      (ui) => ui.integration.key === this._activeTabKey
    )

    if (!activeIntegration) return []

    return this._getFilteredCrmUsersForIntegration(activeIntegration)
  }

  get isTrial() {
    return this._entities.subscriptionStore.isTrial
  }

  get isTwilioNumberVendor() {
    return this._entities.organizationStore.isTwilioNumberVendor
  }

  get hideNewInbox() {
    return this.isTrial || this.isTwilioNumberVendor
  }

  get isEmailInputDisabled() {
    return this._isSendingInvites
  }

  get emailError() {
    return this._emailError
  }

  get sendInvitesDisabled() {
    return !!(
      this._emailError ||
      this.isSendingInvites ||
      this.isLoadingPriceInfo ||
      this.inviteCandidateRows.length === 0
    )
  }

  handleEmailInput = (value: string) => {
    if (this._emailError) {
      this._emailError = null
    }

    if (value.includes(',')) {
      const emails = value
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean)

      const failedEmails: string[] = []

      for (const email of emails) {
        const result = this._addEmail(email, false)
        if (result === AddEmailResult.Invalid || result === AddEmailResult.Duplicate) {
          failedEmails.push(email)
        }
      }

      this._inputValue = ''

      if (failedEmails.length > 0) {
        toastStore.add({
          type: 'error',
          title: "We couldn't add all emails",
          desc: `Some emails are invalid or already invited. Please review and try again: ${failedEmails.join(
            ', '
          )}`,
        })
      }
    } else {
      this._inputValue = value
    }
  }

  addEmailFromInput = () => {
    if (this._inputValue.trim()) {
      this._addEmail(this._inputValue)
    }
  }

  removeInviteCandidate = (id: string) => {
    this._inviteCandidates = this._inviteCandidates.filter((item) => item.id !== id)
  }

  openDropdown = () => {
    if (this.isEmailInputDisabled) return
    this._isDropdownOpen = true
  }

  closeDropdown = () => {
    this._isDropdownOpen = false
  }

  onDropdownClose = () => {
    this.addEmailFromInput()
    this.closeDropdown()
  }

  toggleDropdown = (open: boolean) => {
    if (open) {
      this.openDropdown()
    } else {
      this.onDropdownClose()
    }
  }

  setActiveTab = (tabKey: string) => {
    this._activeTabKey = tabKey
  }

  addEmailFromCrmUser = (crmUser: IntegrationCrmUser) => {
    if (!crmUser.email) return

    const trimmedEmail = crmUser.email.trim().toLowerCase()

    const isDuplicate = this._inviteCandidates.some((item) => item.email === trimmedEmail)
    if (isDuplicate) return

    this._emailError = null
    this._createAndAddCandidate(trimmedEmail, crmUser)
  }

  addEmailFromDropdown = () => {
    const trimmedValue = this._inputValue.trim()
    if (!trimmedValue) return

    this._addEmail(this._inputValue)
  }

  private _validateInviteCandidates = (): boolean => {
    let isValid = true

    for (const candidate of this._inviteCandidates) {
      if (!candidate.email) {
        isValid = false
      }

      if (!candidate.role) {
        isValid = false
      }

      if (candidate.hasEmptyInboxChoice) {
        candidate.setHasInboxError(true)
        isValid = false
      }
    }

    return isValid
  }

  private _validateRolesAgainstInviter = (): boolean => {
    const inviterRole = this.inviterRole

    const hasInvalid = this._inviteCandidates.some(
      (candidate) => !canAssignRole(inviterRole, candidate.role)
    )

    if (hasInvalid) {
      const roleLabel = inviterRole ? roleLabelsMap[inviterRole] : 'unknown'
      toastStore.add({
        type: 'error',
        title: `You cannot assign a role higher than your own. Your current role is ${roleLabel}.`,
      })
      return false
    }

    return true
  }

  private _buildInviteItems = (): IParamsCreateInviteItem[] => {
    return this._inviteCandidates.map((candidate) => {
      const item: IParamsCreateInviteItem = {
        email: candidate.email,
        role: candidate.role,
      }

      if (candidate.isAssignLater) {
        item.assign_later = true
      } else if (candidate.selectedInboxIds && candidate.selectedInboxIds.size > 0) {
        item.team_ids = Array.from(candidate.selectedInboxIds)
      }

      return item
    })
  }

  sendInvites = async (): Promise<boolean> => {
    if (this._inviteCandidates.length === 0) {
      return false
    }

    const isValid = this._validateInviteCandidates()
    if (!isValid) {
      toastStore.add({
        type: 'error',
        title: 'Complete all the required fields',
      })
      return false
    }

    if (!this._validateRolesAgainstInviter()) {
      return false
    }

    const items = this._buildInviteItems()
    if (items.length === 0) {
      return false
    }

    this._isSendingInvites = true

    try {
      await UsersApi.createInvites({ items })
      await this._entities.subscriptionStore.updateSeats()

      runInAction(() => {
        this._inviteCandidates = []
      })

      toastStore.add({
        type: 'success',
        title: pluralize(items.length, 'Invite sent', 'Invites sent'),
      })

      uiStore.changeRoute({
        path: `/${MembersPath.pending}`,
      })

      return true
    } catch (error) {
      console.error('Failed to send invites:', error)

      const axiosError = error as { response?: { data?: IResponseCreateInvitesError } }
      const errorMessage = axiosError.response?.data
        ? parseInvitesError(axiosError.response.data)
        : null

      if (errorMessage) {
        toastStore.add({
          type: 'error',
          title: errorMessage,
        })
      }

      return false
    } finally {
      runInAction(() => {
        this._isSendingInvites = false
      })
    }
  }

  reset = () => {
    this._disposeTableTotalReaction?.()
    this._disposeSeatsReaction?.()
    this._disposeCloseDropdownReaction?.()
    this._inviteCandidates = []
    this._inputValue = ''
    this._isDropdownOpen = false
    this._activeTabKey = ''
    this._emailError = ''
    this._pendingInviteEmails = new Set()
  }
}
